import type { UploadFileValue } from '@/components'
import {
  Button,
  Card,
  Checkbox,
  Combobox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  H1,
  H3,
  MultiFileUpload,
  Textarea,
  Toggles,
  UserCombobox,
} from '@/components'
import { MonthYearPicker } from '@/components/month-year-picker'
import VehicleSelect from '@/components/vehicle-select'
import { cn } from '@/lib/utils'
import { Field } from '@/modules/companies'
import {
  getServicePlanWorkshopName,
  getVehicleExpirationTypeName,
} from '@/modules/vehicles/util'
import { EXPORT_DRIVING_WITHOUT_CARD } from '@/modules/exports/mutations'
import { durationAsSecondsToString, format } from '@/utils'
import { useOnErrorAuto, usePermissions } from '@/utils/hooks'
import { PERMISSIONS } from '@/utils/permissions'
import { isInvalidDate } from '@/utils/time'
import { useMutation, useQuery } from '@apollo/client'
import { useToast } from '@ur-solutions/react-components'
import { useForm, useTranslate } from '@ur-solutions/react-hooks'
import {
  addMinutes,
  differenceInMinutes,
  differenceInSeconds,
  endOfMonth,
  formatISO,
  isSameDay,
  parseISO,
  startOfMonth,
  subMinutes,
} from 'date-fns'
import { CheckIcon, DownloadIcon, MapIcon, RotateCcwIcon } from 'lucide-react'
import React, { useMemo, useState } from 'react'
import { Map as ActivityMap } from '../components/detail'
import {
  ASSIGN_DRIVING_WITHOUT_CARD_EVENT_GROUP,
  RECEIPT_DRIVING_WITHOUT_CARD_EVENT_GROUP,
  UPDATE_DRIVING_WITHOUT_CARD_EVENT_GROUP,
} from '../mutations'
import {
  DRIVER_LOCATIONS_FOR_VEHICLE_PERIOD,
  DRIVING_WITHOUT_CARD_EVENT_GROUPS,
} from '../queries'
import type {
  DriverLocationForMap,
  DrivingWithoutCardEventGroupNode,
  DrivingWithoutCardNearbyVehicleExpirationNode,
  DrivingWithoutCardNearbyVehicleServicePlanNode,
  DrivingWithoutCardNeighbourActivityNode,
} from '../types.graphql'

const DRIVER_LOCATION_PERIOD_PADDING_MINUTES = 30
const EVENT_DRIVER_LOCATION_SEGMENT = 'event'
const BEFORE_DRIVER_LOCATION_SEGMENT = 'before'
const AFTER_DRIVER_LOCATION_SEGMENT = 'after'

const DRIVER_LOCATION_SEGMENTS = [
  BEFORE_DRIVER_LOCATION_SEGMENT,
  EVENT_DRIVER_LOCATION_SEGMENT,
  AFTER_DRIVER_LOCATION_SEGMENT,
] as const

type DriverLocationSegment = (typeof DRIVER_LOCATION_SEGMENTS)[number]

const DRIVER_LOCATION_SEGMENT_COLORS: Record<DriverLocationSegment, string> = {
  [BEFORE_DRIVER_LOCATION_SEGMENT]: '#D97706',
  [EVENT_DRIVER_LOCATION_SEGMENT]: '#0088FF',
  [AFTER_DRIVER_LOCATION_SEGMENT]: '#7C3AED',
}

type ReceiptStatus = 'RECEIPTED' | 'OPEN'

// Most driving without a card is one or two minutes of shunting, which drowns
// out the stretches worth documenting.
const MINIMUM_DRIVING_MINUTES_OPTIONS = [0, 1, 2, 5, 15, 60]

export const DrivingWithoutCard: React.FC = () => {
  const translations = useTranslate({
    unknown: 'common.unknown',
    map: 'common.map',
    loading: 'common.loading',
    noData: 'common.no-data',
    period: 'common.period',
    events: 'common.events',
    duration: 'common.duration',
    driving: 'activities.driving',
    drivingWithoutCard: 'activities.driving-without-card',
    drivingBefore: 'activities.driving-before',
    drivingAfter: 'activities.driving-after',
    mapHint: ['activities.driving-without-card-map-hint', { minutes: '' }],
    driverBefore: 'activities.driver-before',
    driverAfter: 'activities.driver-after',
    vehicleExpirations: 'vehicles.vehicle-expirations',
    vehicleServices: 'vehicles.vehicle-services',
    workshop: 'vehicles.service.workshop',

    month: 'common.month',
    vehicle: 'common.vehicle',
    sameDriverBeforeAndAfter: 'activities.same-driver-before-and-after',
    explanation: 'common.explaination',
    date: 'common.date',
    description: 'common.description',
    save: 'common.save',
    status: 'common.status',
    attachments: 'common.attachments',
    receipted: 'activities.receipted',
    notReceipted: 'activities.not-receipted',
    receipt: 'activities.receipt',
    undoReceipt: 'activities.undo-receipt',
    receiptedByAt: ['activities.receipted-by-at', { user: '', date: '' }],
    all: 'common.all',
    export: 'common.export',
    excel: 'common.excel',
    pdf: 'common.pdf',
    minimumDriving: 'activities.minimum-driving',
    drivingOverMinutes: ['activities.driving-over-minutes', { minutes: '' }],
    assignedDriver: 'activities.assigned-driver',
    noAssignedDriver: 'activities.no-assigned-driver',
    exportStarted: 'exports.success-user-exports',
    error: 'error.generic-server-error',
  })

  const { hasPermissions } = usePermissions()
  const canManage = hasPermissions(PERMISSIONS.activities.change.driveractivity)

  const form = useForm<{
    vehicle: string
    previousDriverUser: string
    nextDriverUser: string
    sameDriverBeforeAndAfter: boolean
    receiptStatus: ReceiptStatus
    minimumDrivingMinutes: number
    datetimeStart: Date
    datetimeEnd: Date
  }>({
    values: {
      vehicle: '',
      previousDriverUser: '',
      nextDriverUser: '',
      sameDriverBeforeAndAfter: false,
      receiptStatus: 'OPEN',
      minimumDrivingMinutes: 0,
      datetimeStart: startOfMonth(new Date()),
      datetimeEnd: endOfMonth(new Date()),
    },
    config: {
      storage: {
        storeFormState: true,
        retrieveFormStateOnMount: true,
        storeFormStateName: 'driving-without-card-filters',
        transformValues: {
          vehicle: val => (typeof val === 'string' ? val : ''),
          previousDriverUser: val => (typeof val === 'string' ? val : ''),
          nextDriverUser: val => (typeof val === 'string' ? val : ''),
          sameDriverBeforeAndAfter: val => val === true,
          receiptStatus: val =>
            val === 'RECEIPTED' || val === 'OPEN' ? val : 'OPEN',
          minimumDrivingMinutes: val => (typeof val === 'number' ? val : 0),
          datetimeStart: val =>
            typeof val === 'string'
              ? isInvalidDate(new Date(val), new Date())
              : undefined,
          datetimeEnd: val =>
            typeof val === 'string'
              ? isInvalidDate(new Date(val), new Date())
              : undefined,
        },
      },
    },
  })

  // A driver only ever gets the groups assigned to them, and the office filters
  // the whole company's list.
  const filterVariables = useMemo(
    () => ({
      datetimeStart: formatISO(form.formValues.datetimeStart),
      datetimeEnd: formatISO(form.formValues.datetimeEnd),
      vehicleId: canManage ? form.formValues.vehicle || null : null,
      previousDriverUserId: canManage
        ? form.formValues.previousDriverUser || null
        : null,
      nextDriverUserId: canManage
        ? form.formValues.nextDriverUser || null
        : null,
      sameDriverBeforeAndAfter:
        canManage && form.formValues.sameDriverBeforeAndAfter ? true : null,
      receipted: canManage
        ? form.formValues.receiptStatus === 'RECEIPTED'
        : null,
      minDurationMinutes: form.formValues.minimumDrivingMinutes || null,
    }),
    [
      canManage,
      form.formValues.datetimeEnd,
      form.formValues.datetimeStart,
      form.formValues.minimumDrivingMinutes,
      form.formValues.nextDriverUser,
      form.formValues.previousDriverUser,
      form.formValues.receiptStatus,
      form.formValues.sameDriverBeforeAndAfter,
      form.formValues.vehicle,
    ]
  )

  const { data } = useQuery(DRIVING_WITHOUT_CARD_EVENT_GROUPS, {
    variables: filterVariables,
  })

  const groups = (data?.drivingWithoutCardEventGroups ??
    []) as DrivingWithoutCardEventGroupNode[]

  const minimumDrivingItems = MINIMUM_DRIVING_MINUTES_OPTIONS.map(minutes => ({
    value: minutes,
    label: minutes
      ? translations.drivingOverMinutes({ minutes: String(minutes) })
      : translations.all,
  }))

  const receiptStatusItems: {
    value: ReceiptStatus
    label: string
  }[] = [
    { value: 'OPEN', label: translations.notReceipted },
    { value: 'RECEIPTED', label: translations.receipted },
  ]

  return (
    <div className="relative box-border px-4 py-4 md:px-[52px] md:py-8 md:pb-[52px]">
      <div className="flex items-center justify-between gap-2">
        <H1>{translations.drivingWithoutCard} </H1>
        {canManage && (
          <DrivingWithoutCardExport
            filterVariables={filterVariables}
            translations={{
              export: translations.export,
              excel: translations.excel,
              pdf: translations.pdf,
              exportStarted: translations.exportStarted,
              error: translations.error,
            }}
          />
        )}
      </div>
      <Card className="mb-4 p-0" contentClassName="grid grid-cols-1">
        <section className="grid grid-cols-1 gap-4 p-4 md:grid-cols-[repeat(auto-fill,minmax(200px,1fr))]">
          <Field>
            <label>{translations.month}</label>
            <MonthYearPicker
              value={form.formValues.datetimeStart}
              className="w-full min-w-0"
              onChange={date => {
                const start = startOfMonth(date)
                const end = endOfMonth(date)
                form.updateForm({
                  datetimeStart: start,
                  datetimeEnd: end,
                })
              }}
            />
          </Field>
          <Field>
            <label>{translations.minimumDriving}</label>
            <Combobox
              mode="single"
              noSearch
              className="min-w-0"
              value={form.formValues.minimumDrivingMinutes}
              items={minimumDrivingItems}
              onChange={minimumDrivingMinutes =>
                form.updateForm({ minimumDrivingMinutes })
              }
            />
          </Field>
          {canManage && (
            <>
              <Field>
                <label>{translations.vehicle}</label>
                <VehicleSelect
                  mode="single"
                  value={form.formValues.vehicle}
                  currentlyOwned
                  clearable
                  onChange={vehicle =>
                    form.updateForm({ vehicle: vehicle?.id ?? '' })
                  }
                />
              </Field>
              <Field>
                <label>{translations.driverBefore}</label>
                <UserCombobox
                  mode="single"
                  value={form.formValues.previousDriverUser}
                  className="min-w-0"
                  filterLeaderGroup
                  onChange={user =>
                    form.updateForm({ previousDriverUser: user })
                  }
                />
              </Field>
              <Field>
                <label>{translations.driverAfter}</label>
                <UserCombobox
                  mode="single"
                  value={form.formValues.nextDriverUser}
                  className="min-w-0"
                  filterLeaderGroup
                  onChange={user => form.updateForm({ nextDriverUser: user })}
                />
              </Field>
              <Field>
                <label>{translations.sameDriverBeforeAndAfter}</label>
                <div className="flex h-[3.875rem] items-center px-4">
                  <Checkbox
                    value={form.formValues.sameDriverBeforeAndAfter}
                    onChange={checked =>
                      form.updateForm({
                        sameDriverBeforeAndAfter: checked === true,
                      })
                    }
                  />
                </div>
              </Field>
              <Field>
                <label>{translations.status}</label>
                <div className="flex h-[3.875rem] items-center">
                  <Toggles
                    wide
                    size="sm"
                    className="min-w-0"
                    value={form.formValues.receiptStatus}
                    options={receiptStatusItems}
                    onChange={receiptStatus =>
                      form.updateForm({ receiptStatus })
                    }
                  />
                </div>
              </Field>
            </>
          )}
        </section>
      </Card>

      <div className="flex flex-col gap-2">
        {groups.map(group => {
          const startDate = parseISO(group.start)
          const endDate = parseISO(group.end)
          const events = group.events.edges.map(edge => edge.node)

          return (
            <Card
              key={group.id}
              className="border border-[#E1E1E1] bg-white px-5 py-4"
            >
              <div className="flex justify-between gap-4 max-md:flex-col">
                <H3>{getVehicleLabel(group, translations.unknown)}</H3>
                <div className="flex items-center gap-3 max-md:flex-col max-md:items-start">
                  <div>
                    <strong>{translations.period}: </strong>{' '}
                    {formatPeriod(startDate, endDate)}
                  </div>
                  <DrivingWithoutCardMapDialog
                    group={group}
                    translations={{
                      map: translations.map,
                      period: translations.period,
                      loading: translations.loading,
                      noData: translations.noData,
                      unknown: translations.unknown,
                      drivingBefore: translations.drivingBefore,
                      drivingWithoutCard: translations.drivingWithoutCard,
                      drivingAfter: translations.drivingAfter,
                      mapHint: translations.mapHint,
                    }}
                  />
                </div>
              </div>
              <div className="my-4 grid grid-cols-2 gap-3 md:grid-cols-4">
                <div className="min-w-0 rounded-md border border-[#E1E1E1] bg-[#F3F3F3] p-3">
                  <span className="mb-1 block text-xs text-[#767689]">
                    {translations.driving}
                  </span>
                  <strong>{group.duration ?? '-'}</strong>
                </div>
                <div className="min-w-0 rounded-md border border-[#E1E1E1] bg-[#F3F3F3] p-3">
                  <span className="mb-1 block text-xs text-[#767689]">
                    {translations.events}
                  </span>
                  <strong>{events.length}</strong>
                </div>
                <div className="min-w-0 rounded-md border border-[#E1E1E1] bg-[#F3F3F3] p-3">
                  <span className="mb-1 block text-xs text-[#767689]">
                    {translations.driverBefore}
                  </span>
                  <strong className="break-words">
                    {getNeighbourDriverLabel(group.previousDriverActivity) ??
                      'N/A'}
                  </strong>
                </div>
                <div className="min-w-0 rounded-md border border-[#E1E1E1] bg-[#F3F3F3] p-3">
                  <span className="mb-1 block text-xs text-[#767689]">
                    {translations.driverAfter}
                  </span>
                  <strong className="break-words">
                    {getNeighbourDriverLabel(group.nextDriverActivity) ?? 'N/A'}
                  </strong>
                </div>
              </div>
              <DrivingWithoutCardGantt group={group} />
              <NearbyVehicleExpirations
                group={group}
                translations={{
                  vehicleExpirations: translations.vehicleExpirations,
                  date: translations.date,
                  description: translations.description,
                }}
              />
              <NearbyVehicleServicePlans
                group={group}
                translations={{
                  vehicleServices: translations.vehicleServices,
                  date: translations.date,
                  workshop: translations.workshop,
                  description: translations.description,
                }}
              />
              <div className="mt-4 grid gap-2">
                {events.map(event => {
                  const eventStart = parseISO(event.start)
                  const eventEnd = parseISO(event.end)

                  return (
                    <div
                      key={event.id}
                      className="grid grid-cols-1 items-center gap-1 rounded-md border border-[#E1E1E1] px-3 py-2 md:grid-cols-[1fr_auto] md:gap-4"
                    >
                      <div>{formatPeriod(eventStart, eventEnd)}</div>
                      <strong>
                        {event.duration ??
                          durationAsSecondsToString(
                            differenceInSeconds(eventEnd, eventStart)
                          )}
                      </strong>
                    </div>
                  )
                })}
              </div>
              <DrivingWithoutCardDocumentation
                group={group}
                canManage={canManage}
                translations={{
                  explanation: translations.explanation,
                  attachments: translations.attachments,
                  save: translations.save,
                  receipt: translations.receipt,
                  undoReceipt: translations.undoReceipt,
                  receiptedByAt: translations.receiptedByAt,
                  assignedDriver: translations.assignedDriver,
                  noAssignedDriver: translations.noAssignedDriver,
                  unknown: translations.unknown,
                }}
              />
            </Card>
          )
        })}
      </div>
    </div>
  )
}

const DrivingWithoutCardExport: React.FC<{
  filterVariables: {
    datetimeStart: string
    datetimeEnd: string
    vehicleId: string | null
    previousDriverUserId: string | null
    nextDriverUserId: string | null
    sameDriverBeforeAndAfter: boolean | null
    receipted: boolean | null
    minDurationMinutes: number | null
  }
  translations: {
    export: string
    excel: string
    pdf: string
    exportStarted: string
    error: string
  }
}> = ({ filterVariables, translations }) => {
  const addToast = useToast()
  const onErrorAuto = useOnErrorAuto()

  const [doExport, { loading }] = useMutation(EXPORT_DRIVING_WITHOUT_CARD, {
    onCompleted(data) {
      if (data?.exportDrivingWithoutCard?.ok) {
        addToast('success', translations.exportStarted)
      } else addToast('error', translations.error)
    },
    onError: onErrorAuto(translations.error),
  })

  // The export takes the filters the list was queried with, so it can never
  // disagree with what is on screen.
  const runExport = (exportFormat: 'EXCEL' | 'PDF') =>
    void doExport({
      variables: { filters: filterVariables, exportFormat },
    })

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button size="sm" variant="outline" disabled={loading}>
          <DownloadIcon />
          {translations.export}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={() => runExport('EXCEL')}>
          {translations.excel}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => runExport('PDF')}>
          {translations.pdf}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

const DrivingWithoutCardMapDialog: React.FC<{
  group: DrivingWithoutCardEventGroupNode
  translations: {
    map: string
    period: string
    loading: string
    noData: string
    unknown: string
    drivingBefore: string
    drivingWithoutCard: string
    drivingAfter: string
    mapHint: (values: { minutes: string }) => string
  }
}> = ({ group, translations }) => {
  const [open, setOpen] = useState(false)
  const [hiddenSegments, setHiddenSegments] = useState<DriverLocationSegment[]>(
    []
  )
  const vehicleVin = getVehicleVin(group)
  const startDate = useMemo(() => parseISO(group.start), [group.start])
  const endDate = useMemo(() => parseISO(group.end), [group.end])

  const { data, loading } = useQuery(DRIVER_LOCATIONS_FOR_VEHICLE_PERIOD, {
    skip: !open || !vehicleVin,
    variables: {
      vehicleVin: vehicleVin ?? '',
      datetimeStart: formatISO(
        subMinutes(startDate, DRIVER_LOCATION_PERIOD_PADDING_MINUTES)
      ),
      datetimeEnd: formatISO(
        addMinutes(endDate, DRIVER_LOCATION_PERIOD_PADDING_MINUTES)
      ),
    },
  })

  const vehicleLocations = useMemo(() => {
    const locations = data?.driverLocationsForVehiclePeriod ?? []

    return getSegmentedVehicleLocations({
      locations,
      vehicleVin,
      startDate,
      endDate,
    })
  }, [data?.driverLocationsForVehiclePeriod, startDate, endDate, vehicleVin])

  const visibleVehicleLocations = useMemo(
    () =>
      vehicleLocations.filter(
        location => !hiddenSegments.includes(location.segment)
      ),
    [hiddenSegments, vehicleLocations]
  )

  const pathColors = useMemo(() => {
    if (!vehicleVin) return {}

    return Object.fromEntries(
      DRIVER_LOCATION_SEGMENTS.map(segment => [
        getVehicleLocationSegmentKey(vehicleVin, segment),
        DRIVER_LOCATION_SEGMENT_COLORS[segment],
      ])
    )
  }, [vehicleVin])

  const toggleSegment = (segment: DriverLocationSegment) =>
    setHiddenSegments(segments =>
      segments.includes(segment)
        ? segments.filter(hidden => hidden !== segment)
        : [...segments, segment]
    )

  const segmentLabels: Record<DriverLocationSegment, string> = {
    [BEFORE_DRIVER_LOCATION_SEGMENT]: translations.drivingBefore,
    [EVENT_DRIVER_LOCATION_SEGMENT]: translations.drivingWithoutCard,
    [AFTER_DRIVER_LOCATION_SEGMENT]: translations.drivingAfter,
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <Button
        size="sm"
        variant="outline"
        disabled={!vehicleVin}
        onClick={() => setOpen(true)}
      >
        <MapIcon />
        {translations.map}
      </Button>
      <DialogContent className="grid h-[min(46rem,calc(100vh-2rem))] w-[min(112rem,calc(100vw-2rem))] !max-w-[min(112rem,calc(100vw-2rem))] grid-rows-[auto_auto_1fr] gap-4 p-5">
        <DialogHeader>
          <DialogTitle>{translations.map}</DialogTitle>
          <DialogDescription>
            {getVehicleLabel(group, translations.unknown)} ·{' '}
            {translations.period}: {formatPeriodText(startDate, endDate)}
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
          <div className="flex flex-wrap items-center gap-2">
            {DRIVER_LOCATION_SEGMENTS.map(segment => {
              const color = DRIVER_LOCATION_SEGMENT_COLORS[segment]
              const hidden = hiddenSegments.includes(segment)
              const hasLocations = vehicleLocations.some(
                location => location.segment === segment
              )

              return (
                <button
                  key={segment}
                  type="button"
                  aria-pressed={!hidden}
                  disabled={!hasLocations}
                  onClick={() => toggleSegment(segment)}
                  className={cn(
                    'inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-semibold transition-colors',
                    hidden
                      ? 'border-stone-200 bg-stone-50 text-stone-400'
                      : 'bg-white',
                    !hasLocations && 'cursor-not-allowed opacity-50'
                  )}
                  style={hidden ? undefined : { borderColor: color, color }}
                >
                  <span
                    className={cn(
                      'h-2 w-2 rounded-full',
                      hidden && 'bg-stone-300'
                    )}
                    style={hidden ? undefined : { backgroundColor: color }}
                  />
                  {segmentLabels[segment]}
                </button>
              )
            })}
          </div>
          <p className="text-xs text-[#767689]">
            {translations.mapHint({
              minutes: String(DRIVER_LOCATION_PERIOD_PADDING_MINUTES),
            })}
          </p>
        </div>
        <div className="min-h-0 overflow-hidden rounded-md border border-[#E1E1E1]">
          {loading ? (
            <div className="flex h-full items-center justify-center text-sm text-[#767689]">
              {translations.loading}
            </div>
          ) : vehicleLocations.length ? (
            <ActivityMap
              className="h-full"
              mapContainerStyle={{ height: '100%', borderTopRightRadius: 0 }}
              pathColors={pathColors}
              snapToRoads={false}
              vehicleLocations={visibleVehicleLocations}
            />
          ) : (
            <div className="flex h-full items-center justify-center text-sm text-[#767689]">
              {translations.noData}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}

const DrivingWithoutCardDocumentation: React.FC<{
  group: DrivingWithoutCardEventGroupNode
  canManage: boolean
  translations: {
    explanation: string
    attachments: string
    save: string
    receipt: string
    undoReceipt: string
    receiptedByAt: (values: { user: string; date: string }) => string
    assignedDriver: string
    noAssignedDriver: string
    unknown: string
  }
}> = ({ group, canManage, translations }) => {
  const savedAttachments = useMemo(
    () =>
      group.attachments.edges.map(edge => ({
        id: edge.node.id,
        name: edge.node.name,
        url: edge.node.fileUrl,
      })),
    [group.attachments.edges]
  )

  const savedSignature = `${group.explanation}|${savedAttachments
    .map(attachment => attachment.id)
    .join(',')}`

  const [explanation, setExplanation] = useState(group.explanation)
  const [files, setFiles] = useState<UploadFileValue[]>(savedAttachments)
  const [syncedSignature, setSyncedSignature] = useState(savedSignature)
  const [saving, setSaving] = useState(false)

  // A save round-trip replaces the server state; drop the draft it was built from.
  if (savedSignature !== syncedSignature) {
    setSyncedSignature(savedSignature)
    setExplanation(group.explanation)
    setFiles(savedAttachments)
  }

  const [updateGroup] = useMutation(UPDATE_DRIVING_WITHOUT_CARD_EVENT_GROUP)
  const [receiptGroup] = useMutation(RECEIPT_DRIVING_WITHOUT_CARD_EVENT_GROUP)
  const [assignGroup] = useMutation(ASSIGN_DRIVING_WITHOUT_CARD_EVENT_GROUP)

  const addedFiles = files.filter((file): file is File => file instanceof File)
  const keptAttachmentIds = new Set(
    files
      .filter(
        (file): file is Exclude<UploadFileValue, File> =>
          !(file instanceof File)
      )
      .map(file => file.id)
  )
  const removedAttachmentIds = savedAttachments
    .filter(attachment => !keptAttachmentIds.has(attachment.id))
    .map(attachment => attachment.id)

  const dirty =
    explanation.trim() !== group.explanation.trim() ||
    addedFiles.length > 0 ||
    removedAttachmentIds.length > 0

  const handleSave = async () => {
    setSaving(true)
    try {
      await updateGroup({
        variables: {
          groupId: group.id,
          explanation,
          attachmentsAdd: addedFiles,
          attachmentsRemove: removedAttachmentIds,
        },
      })
    } finally {
      setSaving(false)
    }
  }

  const handleReceipt = async () => {
    setSaving(true)
    try {
      await receiptGroup({
        variables: { groupId: group.id, receipted: !group.receipted },
        // The list is filtered on the receipt status the group just left.
        refetchQueries: [DRIVING_WITHOUT_CARD_EVENT_GROUPS],
        awaitRefetchQueries: true,
      })
    } finally {
      setSaving(false)
    }
  }

  const handleAssign = async (userId: string) => {
    setSaving(true)
    try {
      await assignGroup({
        variables: { groupId: group.id, userId: userId || null },
      })
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="mt-4 grid min-w-0 gap-2">
      <span className="text-xs text-[#767689]">
        {translations.assignedDriver}
      </span>
      {canManage ? (
        <UserCombobox
          mode="single"
          value={group.assignedUser?.id ?? ''}
          className="min-w-0 md:max-w-80"
          disabled={saving}
          onChange={handleAssign}
        />
      ) : (
        <strong className="text-sm">
          {group.assignedUser?.fullName || translations.noAssignedDriver}
        </strong>
      )}
      <span className="text-xs text-[#767689]">{translations.explanation}</span>
      <Textarea
        aria-label={translations.explanation}
        className="min-h-20 resize-y text-sm"
        rows={3}
        value={explanation}
        onChange={setExplanation}
      />
      <span className="text-xs text-[#767689]">{translations.attachments}</span>
      <MultiFileUpload value={files} onChange={setFiles} />
      <div className="flex items-center justify-end gap-3 max-md:flex-col max-md:items-stretch">
        {group.receipted && (
          <span className="text-xs text-[#767689] max-md:text-center">
            {translations.receiptedByAt({
              user: group.receiptedBy?.fullName || translations.unknown,
              date: group.receiptedAt
                ? format(parseISO(group.receiptedAt), 'dd.MM.yyyy HH:mm')
                : '',
            })}
          </span>
        )}
        <Button
          size="sm"
          variant="outline"
          disabled={!dirty || saving}
          onClick={handleSave}
        >
          {translations.save}
        </Button>
        {canManage && (
          <Button
            size="sm"
            variant={group.receipted ? 'outline' : 'default'}
            disabled={saving}
            onClick={handleReceipt}
          >
            {group.receipted ? <RotateCcwIcon /> : <CheckIcon />}
            {group.receipted ? translations.undoReceipt : translations.receipt}
          </Button>
        )}
      </div>
    </div>
  )
}

const DrivingWithoutCardGantt: React.FC<{
  group: DrivingWithoutCardEventGroupNode
}> = ({ group }) => {
  const groupStart = parseISO(group.start)
  const groupEnd = parseISO(group.end)
  const groupDurationMinutes = Math.max(
    differenceInMinutes(groupEnd, groupStart),
    1
  )

  return (
    <div className="mt-4">
      <div className="mb-1 flex justify-between gap-4 text-xs text-[#767689]">
        <span>{format(groupStart, 'dd.MM.yyyy HH:mm')}</span>
        <span>{format(groupEnd, 'dd.MM.yyyy HH:mm')}</span>
      </div>
      <div className="relative h-9 overflow-hidden rounded-md border border-[#E1E1E1] bg-[repeating-linear-gradient(90deg,#F3F3F3,#F3F3F3_3.5rem,#e9ecef_3.5rem,#e9ecef_3.5625rem)]">
        {group.events.edges.map(({ node: event }) => {
          const startDate = parseISO(event.start)
          const endDate = parseISO(event.end)
          const left =
            (differenceInMinutes(startDate, groupStart) /
              groupDurationMinutes) *
            100
          const width = Math.max(
            (differenceInMinutes(endDate, startDate) / groupDurationMinutes) *
              100,
            0.5
          )
          const clampedLeft = Math.max(0, Math.min(left, 100))
          const availableWidth = Math.max(0, 100 - clampedLeft)
          const clampedWidth = Math.max(
            Math.min(0.5, availableWidth),
            Math.min(width, availableWidth)
          )

          return (
            <div
              key={event.id}
              className="absolute top-[0.45rem] h-5 min-w-1 rounded bg-[#fa1852]"
              title={`${formatPeriodText(startDate, endDate)} · ${
                event.duration ??
                durationAsSecondsToString(
                  differenceInSeconds(endDate, startDate)
                )
              }`}
              style={{
                left: `${clampedLeft}%`,
                width: `${clampedWidth}%`,
              }}
            />
          )
        })}
      </div>
    </div>
  )
}

const NearbyVehicleExpirations: React.FC<{
  group: DrivingWithoutCardEventGroupNode
  translations: {
    vehicleExpirations: string
    date: string
    description: string
  }
}> = ({ group, translations }) => {
  const expirations = group.nearbyVehicleExpirations ?? []

  if (!expirations.length) return null

  return (
    <div className="mt-4 rounded-md border border-[#E1E1E1] bg-[#F8F8F8] p-3">
      <div className="mb-2 text-sm font-semibold">
        {translations.vehicleExpirations}
      </div>
      <div className="grid gap-2">
        {expirations.map(expiration => {
          const expirationType = getVehicleExpirationTypeName({
            name: expiration.expirationType?.name ?? undefined,
          })

          return (
            <div
              key={expiration.id}
              className="grid grid-cols-1 gap-1 rounded border border-[#E1E1E1] bg-white px-3 py-2 text-sm md:grid-cols-[minmax(10rem,1fr)_minmax(10rem,1fr)_minmax(12rem,2fr)]"
            >
              <div className="min-w-0">
                <span className="mb-0.5 block text-xs text-[#767689]">
                  {translations.date}
                </span>
                <strong className="break-words">
                  {formatExpirationPeriod(expiration)}
                </strong>
              </div>
              <div className="min-w-0">
                <span className="mb-0.5 block text-xs text-[#767689]">
                  {translations.vehicleExpirations}
                </span>
                <strong className="break-words">{expirationType}</strong>
              </div>
              <div className="min-w-0">
                <span className="mb-0.5 block text-xs text-[#767689]">
                  {translations.description}
                </span>
                <span className="break-words">
                  {expiration.description ||
                    expiration.responsibleUser?.fullName ||
                    '-'}
                </span>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

/**
 * Workshop visits near the group, the counterpart of NearbyVehicleExpirations.
 *
 * These rows were expirations with a from/to window until FD-571 split them into
 * their own table; without this block the split would silently remove the most
 * useful explanation for a vehicle driving without a card.
 */
const NearbyVehicleServicePlans: React.FC<{
  group: DrivingWithoutCardEventGroupNode
  translations: {
    vehicleServices: string
    date: string
    workshop: string
    description: string
  }
}> = ({ group, translations }) => {
  const servicePlans = group.nearbyVehicleServicePlans ?? []

  if (!servicePlans.length) return null

  return (
    <div className="mt-4 rounded-md border border-[#E1E1E1] bg-[#F8F8F8] p-3">
      <div className="mb-2 text-sm font-semibold">
        {translations.vehicleServices}
      </div>
      <div className="grid gap-2">
        {servicePlans.map(servicePlan => (
          <div
            key={servicePlan.id}
            className="grid grid-cols-1 gap-1 rounded border border-[#E1E1E1] bg-white px-3 py-2 text-sm md:grid-cols-[minmax(10rem,1fr)_minmax(10rem,1fr)_minmax(12rem,2fr)]"
          >
            <div className="min-w-0">
              <span className="mb-0.5 block text-xs text-[#767689]">
                {translations.date}
              </span>
              <strong className="break-words">
                {formatServicePlanPeriod(servicePlan)}
              </strong>
            </div>
            <div className="min-w-0">
              <span className="mb-0.5 block text-xs text-[#767689]">
                {translations.workshop}
              </span>
              <strong className="break-words">
                {getServicePlanWorkshopName(servicePlan) || '-'}
              </strong>
            </div>
            <div className="min-w-0">
              <span className="mb-0.5 block text-xs text-[#767689]">
                {translations.description}
              </span>
              <span className="break-words">
                {servicePlan.description ||
                  servicePlan.name ||
                  servicePlan.responsibleUser?.fullName ||
                  '-'}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

function getNeighbourDriverLabel(
  activity?: DrivingWithoutCardNeighbourActivityNode | null
) {
  return activity?.user?.fullName || activity?.driverCardNumber || null
}

function getVehicleVin(group: DrivingWithoutCardEventGroupNode) {
  return group.vehicle?.vin ?? group.vehicleVin ?? null
}

function getSegmentedVehicleLocations({
  locations,
  vehicleVin,
  startDate,
  endDate,
}: {
  locations: readonly DriverLocationForMap[]
  vehicleVin?: string | null
  startDate: Date
  endDate: Date
}) {
  if (!vehicleVin) return []

  const beforeLocations: DriverLocationForMap[] = []
  const eventLocations: DriverLocationForMap[] = []
  const afterLocations: DriverLocationForMap[] = []

  for (const location of locations) {
    const locationDate = parseISO(location.datetimeStart)

    if (locationDate < startDate) {
      beforeLocations.push(location)
    } else if (locationDate > endDate) {
      afterLocations.push(location)
    } else {
      eventLocations.push(location)
    }
  }

  const firstEventLocation = eventLocations[0]
  const lastEventLocation = eventLocations[eventLocations.length - 1]

  // The surrounding segments reach into the event so the drawn route stays unbroken.
  const segmentLocations: Record<
    DriverLocationSegment,
    DriverLocationForMap[]
  > = {
    [BEFORE_DRIVER_LOCATION_SEGMENT]: firstEventLocation
      ? [...beforeLocations, firstEventLocation]
      : beforeLocations,
    [EVENT_DRIVER_LOCATION_SEGMENT]: eventLocations,
    [AFTER_DRIVER_LOCATION_SEGMENT]: lastEventLocation
      ? [lastEventLocation, ...afterLocations]
      : afterLocations,
  }

  return DRIVER_LOCATION_SEGMENTS.map(segment => ({
    segment,
    vehicleVin: getVehicleLocationSegmentKey(vehicleVin, segment),
    coordinates: getLocationCoordinates(segmentLocations[segment]),
  })).filter(location => location.coordinates.length > 0)
}

function getVehicleLocationSegmentKey(
  vehicleVin: string,
  segment: DriverLocationSegment
) {
  return `${vehicleVin}:${segment}`
}

function getLocationCoordinates(locations: readonly DriverLocationForMap[]) {
  return locations.map(location => ({
    lat: location.latitude,
    lng: location.longitude,
  }))
}

function getVehicleLabel(
  group: DrivingWithoutCardEventGroupNode,
  unknownLabel: string
) {
  return (
    group.vehicle?.registrationNumber ??
    group.vehicleRegistrationNumber ??
    group.vehicle?.vin ??
    group.vehicleVin ??
    unknownLabel
  )
}

function formatServicePlanPeriod(
  servicePlan: DrivingWithoutCardNearbyVehicleServicePlanNode
) {
  if (servicePlan.scheduledEnd) {
    return formatPeriodText(
      parseISO(servicePlan.scheduledStart),
      parseISO(servicePlan.scheduledEnd)
    )
  }

  return format(parseISO(servicePlan.scheduledStart), 'dd.MM.yyyy HH:mm')
}

function formatExpirationPeriod(
  expiration: DrivingWithoutCardNearbyVehicleExpirationNode
) {
  if (expiration.expirationFrom && expiration.expirationTo) {
    return formatPeriodText(
      parseISO(expiration.expirationFrom),
      parseISO(expiration.expirationTo)
    )
  }

  return format(parseISO(expiration.expirationDate), 'dd.MM.yyyy HH:mm')
}

function formatPeriod(startDate: Date, endDate: Date) {
  return <>{formatPeriodText(startDate, endDate)}</>
}

function formatPeriodText(startDate: Date, endDate: Date) {
  const isSameDayValue = isSameDay(startDate, endDate)

  return `${format(startDate, 'dd.MM.yyyy HH:mm')} - ${
    isSameDayValue
      ? format(endDate, 'HH:mm')
      : format(endDate, 'dd.MM.yyyy HH:mm')
  }`
}
